import React, { useEffect, useState } from 'react';
import { ESTADOS_ATENCION } from '../../constants';

const PROXIMA_ACCION_POR_ESTADO = {
  'Pendiente de revisión': 'Revisar consulta administrativa',
  'Esperando cliente': 'Esperar respuesta del cliente',
  'Esperando pago': 'Verificar acreditación',
  'Factura enviada': 'Esperar confirmación del cliente',
  Resuelto: 'Sin acción pendiente',
};

function AtencionPanel({
  datosCliente,
  atencionActual,
  accionRapidaAtencion,
  actualizarAtencionLocal,
  guardarEstadoOperativo,
  guardandoEstado,
  conversacionActiva,
  onVerFactura,
  onGenerarLinkMP,
  diagnosticoBCM,
  estado,
  svcColor,
  svcBg,
}) {
  const convId = conversacionActiva?.id ? String(conversacionActiva.id) : null;

  const fmt = (value, fallback = '-') => {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text || fallback;
  };

  const fmtMoney = (value) => {
    if (value === null || value === undefined) return '-';
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.,-]/g, '').replace(',', '.')) : value;
    if (isNaN(num)) return String(value);
    return '$' + num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtDate = (value) => {
    if (!value) return '-';
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return String(value);
    }
  };

  // ─── Estado administrativo local ───
  const estadoDesdeHook =
    atencionActual?.estadoAdministrativo ||
    atencionActual?.estado_atencion ||
    atencionActual?.estado_administrativo ||
    'Pendiente de revisión';

  const proximaDesdeHook =
    atencionActual?.proximaAccion ||
    atencionActual?.proxima_accion ||
    'Revisar consulta administrativa';

  const observacionDesdeHook = atencionActual?.observacion || '';

  const [estadoVisual, setEstadoVisual] = useState(estadoDesdeHook);
  const [proximaAccionVisual, setProximaAccionVisual] = useState(proximaDesdeHook);
  const [observacionVisual, setObservacionVisual] = useState(observacionDesdeHook);

  useEffect(() => {
    setEstadoVisual(estadoDesdeHook);
    setProximaAccionVisual(proximaDesdeHook);
    setObservacionVisual(observacionDesdeHook);
  }, [convId, estadoDesdeHook, proximaDesdeHook, observacionDesdeHook]);

  const persistirLocal = (patch) => {
    if (!convId || !actualizarAtencionLocal) return;
    actualizarAtencionLocal(convId, {
      ...patch,
      estado_atencion: patch.estadoAdministrativo || patch.estado_atencion,
      estado_administrativo: patch.estadoAdministrativo || patch.estado_administrativo,
      proxima_accion: patch.proximaAccion || patch.proxima_accion,
    });
  };

  const guardarEstadoAtencion = async (patch = {}) => {
    const estadoAdministrativo = patch.estadoAdministrativo ?? estadoVisual;
    const proximaAccion = patch.proximaAccion ?? proximaAccionVisual;
    const observacion = patch.observacion ?? observacionVisual;

    setEstadoVisual(estadoAdministrativo);
    setProximaAccionVisual(proximaAccion);
    setObservacionVisual(observacion);

    persistirLocal({
      estadoAdministrativo,
      estado_atencion: estadoAdministrativo,
      estado_administrativo: estadoAdministrativo,
      proximaAccion,
      proxima_accion: proximaAccion,
      observacion,
    });

    if (guardarEstadoOperativo) {
      try {
        await guardarEstadoOperativo({
          area: 'atencion',
          campo: 'estado_atencion',
          valor: estadoAdministrativo,
          estado_atencion: estadoAdministrativo,
          estado_administrativo: estadoAdministrativo,
          proxima_accion: proximaAccion,
          observacion: observacion || null,
        });
      } catch (error) {
        console.error('Error guardando estado administrativo:', error);
      }
    }

    return true;
  };

  const ejecutarAccionRapida = async (accion) => {
    const map = {
      factura: {
        estadoAdministrativo: 'Factura enviada',
        proximaAccion: 'Esperar confirmación del cliente',
      },
      pago: {
        estadoAdministrativo: 'Esperando pago',
        proximaAccion: 'Verificar acreditación',
      },
      resuelto: {
        estadoAdministrativo: 'Resuelto',
        proximaAccion: 'Sin acción pendiente',
      },
    };

    const next = map[accion];
    if (!next) return;

    await guardarEstadoAtencion(next);

    if (accionRapidaAtencion) {
      try {
        await accionRapidaAtencion(accion);
      } catch (error) {
        console.error('Error ejecutando acción rápida atención:', error);
      }
    }
  };

  // ─── Dirección completa ───
  const buildDireccion = () => {
    if (!datosCliente) return '-';
    const parts = [datosCliente.direccion];
    if (datosCliente.entre_calles_1 || datosCliente.entre_calles_2) {
      const entres = [datosCliente.entre_calles_1, datosCliente.entre_calles_2].filter(Boolean).join(' y ');
      if (entres) parts.push(`(entre ${entres})`);
    }
    if (datosCliente.ciudad) parts.push(`- ${datosCliente.ciudad}`);
    const result = parts.filter(Boolean).join(' ');
    return result || '-';
  };

  return (
    <>
      {/* ─── Estado administrativo ─── */}
      <div className="ctx-card">
        <div className="ctx-card-header">
          <span className="ctx-card-title">Estado administrativo</span>
          {guardandoEstado && (
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>guardando...</span>
          )}
        </div>

        <div className="ctx-details">
          <div style={{ marginBottom: 8 }}>
            <span className="ctx-label">Estado administrativo:</span>
            <select
              className="form-select"
              style={{ width: '100%', marginTop: 6 }}
              value={estadoVisual}
              onChange={(e) => {
                const nuevoEstado = e.target.value;
                const proximaAccion =
                  PROXIMA_ACCION_POR_ESTADO[nuevoEstado] ||
                  proximaAccionVisual ||
                  'Revisar caso administrativo';
                guardarEstadoAtencion({ estadoAdministrativo: nuevoEstado, proximaAccion });
              }}
            >
              {ESTADOS_ATENCION.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 8 }}>
            <span className="ctx-label">Próxima acción:</span>
            <input
              className="form-input"
              style={{ width: '100%', marginTop: 6 }}
              value={proximaAccionVisual}
              onChange={(e) => {
                const value = e.target.value;
                setProximaAccionVisual(value);
                persistirLocal({
                  estadoAdministrativo: estadoVisual,
                  proximaAccion: value,
                  observacion: observacionVisual,
                });
              }}
              onBlur={(e) => {
                guardarEstadoAtencion({ proximaAccion: e.target.value });
              }}
            />
          </div>

          <div>
            <span className="ctx-label">Observación:</span>
            <textarea
              className="form-input"
              style={{ width: '100%', marginTop: 6, minHeight: 70, resize: 'vertical' }}
              value={observacionVisual}
              onChange={(e) => {
                const value = e.target.value;
                setObservacionVisual(value);
                persistirLocal({
                  estadoAdministrativo: estadoVisual,
                  proximaAccion: proximaAccionVisual,
                  observacion: value,
                });
              }}
              onBlur={(e) => {
                guardarEstadoAtencion({ observacion: e.target.value });
              }}
            />
          </div>
        </div>
      </div>

      {/* ─── Plan actual ─── */}
      <div className="ctx-card">
        <div className="ctx-card-header">
          <span className="ctx-card-title">Plan actual</span>
          {datosCliente?.tipo_plan && (
            <span className="status-badge" style={{ background: '#3b82f622', color: '#60a5fa', fontSize: 11 }}>
              {datosCliente.tipo_plan}
            </span>
          )}
        </div>
        <div className="plan-row">
          <div>
            <div className="plan-name">{fmt(datosCliente?.plan)}</div>
            {datosCliente?.precio && (
              <div className="plan-price">
                <strong>{fmtMoney(datosCliente.precio)}</strong> /mes
              </div>
            )}
            {(datosCliente?.velocidad_down || datosCliente?.velocidad_up) && (
              <div className="plan-price">
                Velocidad: <strong>{fmt(datosCliente?.velocidad_down)} / {fmt(datosCliente?.velocidad_up)} Mbps</strong>
              </div>
            )}
            {datosCliente?.proximo_pago && (
              <div className="plan-next">
                Próximo pago: <strong>{fmtDate(datosCliente.proximo_pago)}</strong>
              </div>
            )}
          </div>
          <span className="plan-icon">📶</span>
        </div>
      </div>

      {/* ─── Facturación y pagos ─── */}
      <div className="ctx-card">
        <div className="ctx-card-header">
          <span className="ctx-card-title">Facturación y pagos</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div className="billing-detail">
            <span className="billing-label">Deuda</span>
            <span className={'billing-value ' + ((datosCliente?.deuda || 0) > 0 ? 'debt' : 'paid')}>
              ${datosCliente?.deuda ?? 0}
            </span>
          </div>
          <div className="billing-detail">
            <span className="billing-label">Saldo a favor</span>
            <span className="billing-value paid">${datosCliente?.saldo_a_favor ?? 0}</span>
          </div>

          {datosCliente?.ultima_factura && (
            <>
              <div className="billing-divider" />
              <div className="billing-detail">
                <span className="billing-label">Factura N°</span>
                <span className="billing-value">{fmt(datosCliente.ultima_factura)}</span>
              </div>
            </>
          )}
          {datosCliente?.fecha_factura && (
            <div className="billing-detail">
              <span className="billing-label">Fecha</span>
              <span className="billing-value">{fmtDate(datosCliente.fecha_factura)}</span>
            </div>
          )}
          {datosCliente?.monto_factura && (
            <div className="billing-detail">
              <span className="billing-label">Monto</span>
              <span className="billing-value">{fmtMoney(datosCliente.monto_factura)}</span>
            </div>
          )}
          {datosCliente?.vencimiento && (
            <div className="billing-detail">
              <span className="billing-label">Vencimiento</span>
              <span className={'billing-value ' + (new Date(datosCliente.vencimiento) < new Date() ? 'debt' : 'pending')}>
                {fmtDate(datosCliente.vencimiento)}
              </span>
            </div>
          )}
        </div>

        <div className="billing-actions">
          <button className="billing-btn" onClick={onVerFactura}>
            📄 Ver factura
          </button>
          {datosCliente?.factura_url && (
            <button className="billing-btn accent" onClick={() => window.open(datosCliente.factura_url, '_blank')}>
              ⬇️ Descargar PDF
            </button>
          )}
          <button className="billing-btn mp" onClick={onGenerarLinkMP}>
            💳 Generar link Mercado Pago
          </button>
          {datosCliente?.alias_siro && (
            <button className="billing-btn siro" onClick={() => {
              navigator.clipboard.writeText(datosCliente.alias_siro);
            }}>
              🏦 Copiar alias SIRO: {datosCliente.alias_siro}
            </button>
          )}
        </div>
      </div>

      {/* ─── Acciones rápidas administrativas ─── */}
      <div className="ctx-card">
        <div className="ctx-card-header">
          <span className="ctx-card-title">Acciones administrativas</span>
        </div>
        <button className="service-action" onClick={() => ejecutarAccionRapida('factura')}>
          ✉️ Marcar factura enviada
        </button>
        <button className="service-action" onClick={() => ejecutarAccionRapida('pago')}>
          ⏳ Marcar esperando pago
        </button>
        <button className="service-action" onClick={() => ejecutarAccionRapida('resuelto')} style={{ color: '#4ade80' }}>
          ✅ Marcar resuelto
        </button>
      </div>

      {/* ─── Dirección del servicio ─── */}
      {datosCliente && (datosCliente.direccion || datosCliente.ciudad) && (
        <div className="ctx-card">
          <div className="ctx-card-header">
            <span className="ctx-card-title">Dirección del servicio</span>
          </div>
          <div className="address-line">{buildDireccion()}</div>
          {datosCliente.nodo && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
              Nodo: <span style={{ color: 'var(--text)', fontWeight: 500 }}>{datosCliente.nodo}</span>
            </div>
          )}
          {datosCliente.direccion && (
            <button
              className="map-btn"
              onClick={() => {
                const q = encodeURIComponent(
                  [datosCliente.direccion, datosCliente.ciudad].filter(Boolean).join(', ')
                );
                window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank');
              }}
            >
              📍 Ver en mapa
            </button>
          )}
        </div>
      )}

      {/* ─── Estado del servicio ─── */}
      {datosCliente && (
        <div className="ctx-card">
          <div className="ctx-card-header">
            <span className="ctx-card-title">Estado del servicio</span>
            <span className="status-badge" style={{ background: svcBg, color: svcColor, fontSize: 11 }}>
              {estado}
            </span>
          </div>
          <div className="ctx-details">
            <div className="ctx-row">
              <span className="ctx-label">Servicio</span>
              <span className="ctx-value" style={{ color: svcColor }}>{estado}</span>
            </div>
            <div className="ctx-row">
              <span className="ctx-label">Estado actual</span>
              <span className="ctx-value">{diagnosticoBCM?.diagnostico_resumen || 'Sin fallas reportadas'}</span>
            </div>
            <div className="ctx-row">
              <span className="ctx-label">Última conexión</span>
              <span className="ctx-value">
                {datosCliente.ultima_conexion
                  ? fmtDate(datosCliente.ultima_conexion)
                  : (diagnosticoBCM?.ultima_conexion || '-')}
              </span>
            </div>
            <div className="ctx-row">
              <span className="ctx-label">Equipos</span>
              <span className="ctx-value">{datosCliente.equipos || diagnosticoBCM?.equipo || '-'}</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── Portal cliente ─── */}
      {datosCliente && (datosCliente.portal_usuario || datosCliente.portal_password) && (
        <div className="ctx-card">
          <div className="ctx-card-header">
            <span className="ctx-card-title">Portal cliente</span>
          </div>
          <div className="ctx-details">
            {datosCliente.portal_usuario && (
              <div className="ctx-row">
                <span className="ctx-label">Usuario</span>
                <span className="ctx-value" style={{ fontFamily: 'monospace' }}>{datosCliente.portal_usuario}</span>
              </div>
            )}
            {datosCliente.portal_password && (
              <div className="ctx-row">
                <span className="ctx-label">Contraseña</span>
                <span className="ctx-value" style={{ fontFamily: 'monospace' }}>{datosCliente.portal_password}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default React.memo(AtencionPanel);
